import React from 'react';
import { Button } from '../../components/ui/button';
import { Target, Phone, Mail, TrendingUp, Zap, Users, CheckCircle } from 'lucide-react';
import HeroSection from '../../components/HeroSection';
import WaveOverlay from '../../components/WaveOverlay';

const RealEstate = ({ onBookMeeting }) => {
  const solutions = [
    {
      icon: <Target className="w-8 h-8 text-blue-600" />,
      title: "CRM & System Integration",
      subtitle: "Seamless Lead Flow, No More Leaks:",
      description: "We connect all your existing tools (website, lead forms, portals) to your CRM (yours or ours). We ensure every new prospect is instantly logged, prioritized, and assigned to a Nurture Campaign."
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-green-600" />,
      title: "'Top-of-Mind' Nurture Campaigns",
      subtitle: "Automated Relationship Engine:",
      description: "We design and implement customized outreach campaigns (email, text, physical mail triggers) to keep you top-of-mind over the entire client lifecycle. Stay relevant with market updates and homeowner tips, all on auto-pilot."
    },
    {
      icon: <Phone className="w-8 h-8 text-purple-600" />,
      title: "The AI Receptionist & Appointment Setter",
      subtitle: "24/7 Phone Automation:",
      description: "We set up an Interactive Voicemail System that screens spam, books appointments directly into your calendar, and automatically triggers custom follow-up emails and texts to legitimate clients."
    },
    {
      icon: <Mail className="w-8 h-8 text-orange-500" />,
      title: "Property-Specific Call-In Info",
      subtitle: "Instant Listing Information & Lead Capture:",
      description: "We set up a unique phone number for every listing. Callers can instantly hear detailed property information, request a showing, or have digital info sent directly to their phone, capturing the lead and automating initial engagement."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection>
        <div className="max-w-5xl mx-auto text-center space-y-6 relative z-40">
          <div className="flex items-center justify-center space-x-2 text-orange-300">
            <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Industries
            </span>
            <span>» Real Estate & Mortgage</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
            <span className="rainbow-text">Automate the Admin.</span> Master the Follow-Up. Close More Deals.
          </h1>
          <h2 className="text-2xl lg:text-3xl font-semibold text-blue-100">
            Stop Chasing Leads. Start Nurturing a Pipeline.
          </h2>
          <p className="text-xl text-blue-100 leading-relaxed max-w-4xl mx-auto">
            As a former residential broker, our founder knows the real challenge isn't getting leads—it's managing them, 
            staying <strong>Top-of-Mind</strong> for years, and converting past clients. We build the systems that put 
            your business on autopilot.
          </p>
          <Button 
            onClick={onBookMeeting}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 relative z-50"
          >
            Schedule Your Free "Systems & Efficiency" Audit
          </Button>
        </div>
      </HeroSection>

      {/* Core Solutions Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Core Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored automation and workflow systems designed specifically for real estate and mortgage professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {solutions.map((solution, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-200">
                <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full mb-6 mx-auto shadow-md">
                  {solution.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">{solution.title}</h3>
                <h4 className="text-lg font-semibold text-blue-600 mb-3 text-center">{solution.subtitle}</h4>
                <p className="text-gray-600 leading-relaxed text-center">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategy & Credibility Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="flex items-center justify-center w-20 h-20 bg-blue-600 rounded-full mb-6 mx-auto">
              <Users className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">Targeted Strategy. Guaranteed Results.</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              Digital efforts are easy to overcomplicate. Our founder, a successful former broker, offers <strong>1:1 Strategy 
              & Coaching Sessions</strong> to ensure every dollar and minute spent on digital marketing is <strong>moving the 
              needle</strong> for your specific market. We help you target the right clients with the right message.
            </p>
            <div className="inline-flex items-center space-x-3 bg-green-50 border-2 border-green-200 rounded-lg px-6 py-4 mt-8">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <span className="text-lg font-semibold text-green-800">We are an Industry Partner, not just a tech team.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 hero-gradient text-white relative">
        <WaveOverlay inverted={true} color="fill-white" />
        <div className="container mx-auto px-4 text-center relative z-30">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Stop Leaving Money on the Table
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
            Let us turn your past client list and new leads into a reliable, efficient referral engine.
          </p>
          <Button 
            onClick={onBookMeeting}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105"
          >
            Schedule Your Free Workflow Consultation Now
          </Button>
        </div>
        <WaveOverlay />
      </section>
    </div>
  );
};

export default RealEstate;

